import type { HotelResultsNotice } from './hotel_results_list.js';
import type { HotelOfferPagePhase } from './use_hotel_offer_pages.js';

/** O aviso que a lista COMPLETA pede, ou `null` quando ela é a resposta. */
export type HotelOfferPagesNoticeKind =
  | 'loadingAll'
  | 'staleRetrying'
  | 'unavailable'
  | 'stale'
  | 'error';

/** Em que pé está a busca vencida que o host refaz sozinho. */
export type HotelExpiredRetryState = {
  /** Uma busca automática está em voo agora. */
  readonly retrying: boolean;
  /** O orçamento acabou e a lista segue vencida: é indisponibilidade. */
  readonly exhausted: boolean;
};

const NO_EXPIRED_RETRY: HotelExpiredRetryState = { retrying: false, exhausted: false };

/**
 * Qual aviso o painel deve dar sobre a lista COMPLETA — `null` quando a lista em
 * cena é a resposta e não há nada a explicar.
 *
 * Existe porque as ofertas do bloco aparecem no painel em três situações que a
 * tela não distinguia: a página a caminho, a busca vencida e a leitura falhada.
 * As três mostravam "6 hotéis" caladas, e o viajante não tinha como saber que
 * faltava algo (nem que a lista completa tinha 35).
 *
 * `loadingAll` só no primeiro pedido (`!committed`): depois disso a lista em cena
 * é uma página de verdade, e anunciar carga a cada troca de corte viraria ruído.
 */
export function hotelOfferPagesNoticeKind(
  page: { phase: HotelOfferPagePhase; committed: boolean },
  /** Enquanto a busca vencida se refaz o painel ESPERA em vez de acusar, e
   *  quando as tentativas acabam o diagnóstico deixa de ser "expirou" — a lista
   *  está indisponível. O aviso seco de expiração sobra para quem não pode
   *  buscar (card congelado). */
  expiredRetry: HotelExpiredRetryState = NO_EXPIRED_RETRY,
): HotelOfferPagesNoticeKind | null {
  if (page.phase === 'stale') {
    if (expiredRetry.retrying) return 'staleRetrying';
    return expiredRetry.exhausted ? 'unavailable' : 'stale';
  }
  if (page.phase === 'error') return 'error';
  if (page.phase === 'loading' && !page.committed) return 'loadingAll';
  return null;
}

export type HotelOfferPagesNoticeCopy = {
  loadingAllTitle: string;
  staleRetryingTitle: string;
  unavailableTitle: string;
  unavailableMessage: string;
  staleTitle: string;
  staleMessage: string;
  /** A ação de refazer a busca, na vencida e na indisponível. */
  searchAgainLabel: string;
  errorTitle: string;
  errorMessage: string;
  retryLabel: string;
};

/**
 * O aviso da página com a cópia e a ação de cada estado.
 *
 * A busca VENCIDA se refaz sozinha, com os mesmos filtros (o host faz isso):
 * expirar é problema do fornecedor, não escolha do viajante, e uma tela de erro
 * no lugar da lista faz ele repetir à mão o que a tela já sabe pedir. Enquanto a
 * busca nova roda, o aviso é de ESPERA.
 *
 * Esgotadas as tentativas e a lista ainda vencendo, o diagnóstico muda: não é
 * uma sessão velha, é a lista completa INDISPONÍVEL. O botão continua ali —
 * insistir passa a ser decisão do viajante, não da tela.
 *
 * Carregando não tem mensagem: a página o mostra como a linha da meta, e ali só
 * o título cabe sem virar parágrafo.
 */
export function hotelOfferPagesNotice(input: {
  kind: HotelOfferPagesNoticeKind | null;
  copy: HotelOfferPagesNoticeCopy;
  /** Refaz a busca inteira (vencida ou indisponível). */
  onSearchAgain: () => void;
  /** Repete a leitura da página que falhou. */
  onRetry: () => void;
}): HotelResultsNotice | undefined {
  const { kind, copy, onSearchAgain, onRetry } = input;
  switch (kind) {
    case 'loadingAll':
      return { tone: 'loading', title: copy.loadingAllTitle };
    case 'staleRetrying':
      return { tone: 'loading', title: copy.staleRetryingTitle };
    case 'unavailable':
      return {
        tone: 'error',
        title: copy.unavailableTitle,
        message: copy.unavailableMessage,
        actionLabel: copy.searchAgainLabel,
        onAction: onSearchAgain,
      };
    case 'stale':
      return {
        tone: 'warning',
        title: copy.staleTitle,
        message: copy.staleMessage,
        actionLabel: copy.searchAgainLabel,
        onAction: onSearchAgain,
      };
    case 'error':
      return {
        tone: 'error',
        title: copy.errorTitle,
        message: copy.errorMessage,
        actionLabel: copy.retryLabel,
        onAction: onRetry,
      };
    case null:
    default:
      return undefined;
  }
}

/** O desfecho do "buscar nesta área" (#3984), do host. */
export type HotelSearchAreaOutcome =
  | { readonly phase: 'pending' }
  | { readonly phase: 'empty' }
  | { readonly phase: 'error'; readonly onRetry: () => void };

export type HotelSearchAreaNoticeCopy = {
  searchingTitle: string;
  emptyTitle: string;
  emptyMessage: string;
  errorTitle: string;
  errorMessage: string;
  retryLabel: string;
};

/** O desfecho da busca da área (#3984) vence o aviso da lista: é o que a pessoa
 *  acabou de pedir, e a lista em cena ainda é a de antes (ou nenhuma). */
export function hotelSearchAreaNotice(
  status: HotelSearchAreaOutcome | undefined,
  copy: HotelSearchAreaNoticeCopy,
): HotelResultsNotice | undefined {
  if (!status) return undefined;
  if (status.phase === 'pending') return { tone: 'loading', title: copy.searchingTitle };
  if (status.phase === 'empty') {
    return { tone: 'warning', title: copy.emptyTitle, message: copy.emptyMessage };
  }
  return {
    tone: 'error',
    title: copy.errorTitle,
    message: copy.errorMessage,
    actionLabel: copy.retryLabel,
    onAction: status.onRetry,
  };
}
